import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, tap, EMPTY, switchMap } from 'rxjs'; 
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http'; 
import { User } from '../models/user';
import { SubscriptionService } from './subscription.service';
import { SecureStorageService } from './secure-storage.service';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;
  private readonly BASE_URL: string;
  private API_VERSION = 'v1';
  isRefreshing = false;
  private subscriptionService: SubscriptionService;

  constructor(
    private http: HttpClient,
    private injector: Injector,
    private secureStorage: SecureStorageService
  ) {
    this.BASE_URL = environment['apiUrls'].baseUrl;
    // Initialize with null, load user asynchronously
    this.currentUserSubject = new BehaviorSubject<User>(null);
    this.currentUser = this.currentUserSubject.asObservable();
    // Load user from secure storage asynchronously
    this.loadStoredUser();
  }

  /**
   * Loads user from secure storage asynchronously
   */
  private async loadStoredUser(): Promise<void> {
    try {
      const storedUser = await this.secureStorage.getItem<User>('currentUser', true);
      if (storedUser) {
        this.currentUserSubject.next(storedUser);
      }
    } catch (error) {
      // If loading fails, user is not logged in
      this.currentUserSubject.next(null);
    }
  }

  /**
   * Lazy getter for SubscriptionService to avoid circular dependency
   */
  private get subscriptionServiceInstance(): SubscriptionService {
    if (!this.subscriptionService) {
      this.subscriptionService = this.injector.get(SubscriptionService);
    }
    return this.subscriptionService;
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  public get isRefresh(): boolean {
    return this.isRefreshing;
  }

  updateCurrentUser(user: User) {
    this.currentUserSubject.next(user);
  }

  validateTenant(userInfo) {
    const url = `${this.BASE_URL}/${this.API_VERSION}/auth/tenant`;
    const body = userInfo;
    return this.http.post<any>(url, body);
  }

  signUp(userInfo) {
    const url = `${this.BASE_URL}/${this.API_VERSION}/auth/signup`;
    const body = userInfo;
    return this.http.post<any>(url, body);
  }

  signIn(userInfo): Observable<any> {
    const url = `${this.BASE_URL}/${this.API_VERSION}/auth/login`;
    return this.http.post<any>(url, userInfo).pipe(
      switchMap((user) => {
        user.username = userInfo['userName'];
        const decodedPayload = jwtDecode<any>(user.token);
        user.roles = decodedPayload.roles;
        user.accessRights = decodedPayload.accessRights;
        // Store user securely with encryption and validation (async)
        this.secureStorage.setItem('currentUser', user, true).then(() => {
          this.currentUserSubject.next(user);
        }).catch((error) => {
          console.error('Error storing user:', error);
        });
        // Update subject immediately for synchronous access
        this.currentUserSubject.next(user);
        return this.subscriptionServiceInstance.load().pipe(
          map(() => user)
        );
      })
    );
  }
  

  logout() {
    if(this.currentUserValue){
      const body = {
        refreshToken: this.currentUserValue?.refreshToken
      };
      // Remove from secure storage
      this.secureStorage.removeItem('currentUser');
      this.currentUserSubject.next(null);
      this.subscriptionServiceInstance.clear();
      const url = `${this.BASE_URL}/${this.API_VERSION}/auth/logout`;
      return this.http.post<any>(url, body).pipe(
        tap(() => {
          // Ensure removal even if API call fails
          this.secureStorage.removeItem('currentUser');
          this.currentUserSubject.next(null);
          this.subscriptionServiceInstance.clear();
        }),
        catchError((error) => {
          return of({}); 
        })
      );
    }
    return EMPTY;
  }

  refreshToken() {
    if(this.currentUserValue){
      // Validate token expiration before attempting refresh
      if (this.currentUserValue.token && !this.secureStorage.validateToken(this.currentUserValue.token)) {
        // Token is expired, logout user
        this.logout();
        return EMPTY;
      }
      
      const body = {
        refreshToken: this.currentUserValue?.refreshToken
      };
      this.isRefreshing = true;
      const url = `${this.BASE_URL}/${this.API_VERSION}/auth/refresh`;
      return this.http.post<any>(url, body).pipe(
        tap((response) => {
          this.isRefreshing = false;
          // Update token in secure storage if refresh is successful (async)
          if (response && response.accessToken) {
            const updatedUser = { ...this.currentUserValue, token: response.accessToken };
            this.secureStorage.setItem('currentUser', updatedUser, true).then(() => {
              this.currentUserSubject.next(updatedUser);
            }).catch((error) => {
              console.error('Error storing updated user:', error);
            });
            // Update subject immediately for synchronous access
            this.currentUserSubject.next(updatedUser);
          }
        }),
        map((response) => response),
        catchError((error) => {
          this.isRefreshing = false;
          return of({}); 
        })
      );
    }
    return EMPTY;
  }

  public hasRole(requiredRole: string): boolean {
    return this.currentUserValue?.roles?.includes(requiredRole) ?? false;
  }

  public hasAccessRight(requiredPermission: string | string[]): boolean {
    const userPermissions = this.currentUserValue?.accessRights || [];
    const requiredPermissions = Array.isArray(requiredPermission) 
      ? requiredPermission 
      : [requiredPermission];
    return requiredPermissions.some(permission => userPermissions.includes(permission));
  }
}
